import * as React from 'react'
import { RUN_PHASE_LABELS, type RunPhase, type RunStateDto } from '@acs/shared'
import { cn } from '@/lib/cn'

/**
 * Where a run has got to, in five words.
 *
 * The same shape as `PipelineProgress` — five segments, one line of plain
 * English underneath — because that is the readout operators already know and
 * asked to keep. The difference is what drives it: `PipelineProgress` reads the
 * legacy `PIPELINE_STEPS` string off `customer_sessions`, which a run started by
 * THIS system never writes. This reads `RunPhase`, which arrives over the stream
 * in ~80ms.
 *
 * The two are kept as separate components rather than one with a mode flag: they
 * share a look, not a data model, and the union type of "either a legacy step
 * string or a run phase" is exactly the kind of parameter that turns a small
 * component into a branch nobody can read.
 *
 * Colour here is *status*, so it is green/amber/red — never the accent. Indigo
 * means "you selected this"; a pipeline that turned indigo as it succeeded would
 * put selection and progress on the same hue.
 */

/* ------------------------------------------------------------------ *
 * Phases
 * ------------------------------------------------------------------ */

interface Segment {
  key: string
  label: string
  /** Run phases that light this segment as the current one. */
  phases: readonly RunPhase[]
}

/**
 * Five segments, matching the legacy stepper's labels one for one.
 *
 * `starting` covers the browser launch and the CSRF fetch, which is what
 * "Validate" meant in the old pipeline; `holding` is the heartbeat, which is
 * "Keep alive". The middle three line up exactly.
 */
const SEGMENTS: readonly Segment[] = [
  { key: 'validate', label: 'Validate', phases: ['starting'] },
  { key: 'auth', label: 'Authenticate', phases: ['signing_in'] },
  { key: 'otp', label: 'Verify OTP', phases: ['awaiting_otp'] },
  { key: 'apply', label: 'Apply', phases: ['applying'] },
  { key: 'alive', label: 'Keep alive', phases: ['holding'] },
]

const PHASE_SEGMENT: Readonly<Record<RunPhase, number>> = Object.freeze({
  idle: -1,
  starting: 0,
  signing_in: 1,
  awaiting_otp: 2,
  applying: 3,
  holding: 4,
  // A terminal phase does not have a segment of its own — it colours whichever
  // one the run died on, which is what `RunPipeline` uses `lastStepKey` for.
  failed: -1,
  stopped: -1,
})

/**
 * Which segment a terminal run died in, recovered from its last step.
 *
 * Without this a failed run collapses to "no segment", so the bar goes blank at
 * exactly the moment it is being read to find out how far things got. The step
 * key survives the phase change, so it is the honest answer.
 */
const STEP_SEGMENT: Readonly<Record<string, number>> = Object.freeze({
  browser: 0,
  csrf: 0,
  sign_in: 1,
  confirm_otp: 1,
  authorize: 1,
  otp_wait: 2,
  /*
   * Watching for a job and shift to apply to belongs to Apply.
   *
   * Unlike `watch_schedule` below — which runs alongside the heartbeat once a
   * seat is held — this happens BEFORE any application exists, and it is the
   * whole of what the run is doing. Without an entry here a run parked for hours
   * resolves to -1 and paints a blank bar, which is exactly when it is read.
   */
  match_schedule: 3,
  /*
   * The rest of the road to an application, all of it Apply.
   *
   * A run can now END on two of these: the gate stops a customer who is not
   * VERIFIED on the `id_verification` row, and a link that will not answer
   * `success: true` stops on `link_application`. A failed run falls back to
   * this map by its last step, so without these entries exactly those runs —
   * the ones being read to find out what happened — painted a blank bar.
   */
  application_lookup: 3,
  available_schedules: 3,
  id_verification: 3,
  link_application: 3,
  create_application: 3,
  update_application: 3,
  keep_alive: 4,
  /*
   * The watch belongs to the LAST segment, not to Apply.
   *
   * It runs concurrently with the heartbeat, for as long as the seat is held —
   * hours after the application was created. Mapping it to Apply would rewind
   * the bar to segment 3 on every stopped run that had been watching, which
   * reads as "it never got past applying" about a run that spent six hours past
   * it.
   */
  watch_schedule: 4,
  /*
   * The form wizard is also the last segment, and for a stronger reason than
   * the watch.
   *
   * It only runs once a shift has been CLAIMED, so a run that reaches it has
   * completed everything the five segments describe. There is no segment for
   * "finishing the paperwork" because the bar tracks getting the seat, and that
   * is already over — showing it anywhere earlier would rewind the bar on the
   * one run that went furthest.
   */
  onboarding_step: 4,
  onboarding_save: 4,
  onboarding_advance: 4,
  onboarding_nhe: 4,
  onboarding_done: 4,
  onboarding_socket: 4,
})

function segmentIndex(state: RunStateDto): number {
  const byPhase = PHASE_SEGMENT[state.phase]
  if (byPhase >= 0) return byPhase
  if (state.stepKey !== null) return STEP_SEGMENT[state.stepKey] ?? -1
  return -1
}

/* ------------------------------------------------------------------ *
 * Tone
 *
 * Only the CURRENT segment carries the run's colour. Completed segments are
 * always green, so a failed run reads as green up to the point it died and one
 * red segment where it did — rather than a bar of red that hides where it got
 * to.
 * ------------------------------------------------------------------ */

interface Style {
  fill: string
  text: string
  /** A working run pulses. Nothing else does — motion means "in progress". */
  pulse: boolean
}

function styleFor(phase: RunPhase): Style {
  switch (phase) {
    case 'failed':
      return { fill: 'bg-danger', text: 'text-danger', pulse: false }
    case 'awaiting_otp':
      return { fill: 'bg-warn', text: 'text-warn', pulse: true }
    case 'stopped':
    case 'idle':
      return { fill: 'bg-neutral', text: 'text-muted', pulse: false }
    case 'holding':
      return { fill: 'bg-live', text: 'text-live', pulse: true }
    default:
      return { fill: 'bg-ok', text: 'text-ok', pulse: true }
  }
}

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

export const RunPipeline = React.memo(function RunPipeline({
  state,
  className,
}: {
  state: RunStateDto
  className?: string
}) {
  const index = segmentIndex(state)
  const style = styleFor(state.phase)
  const current = index >= 0 ? SEGMENTS[index] : undefined

  // The detail line the server already computes ("waf-token", "beat 39 ·
  // JOB_SELECTED") beats any label this component could invent, because it comes
  // from the step that is actually running.
  const caption =
    state.detail ??
    (state.stepKey ? state.stepKey.replace(/_/g, ' ') : RUN_PHASE_LABELS[state.phase])

  const valueNow = index + 1
  const valueText = current
    ? `${current.label}, phase ${valueNow} of ${SEGMENTS.length}. ${caption}.`
    : RUN_PHASE_LABELS[state.phase]

  return (
    <div
      role="progressbar"
      aria-label="Run progress"
      aria-valuemin={0}
      aria-valuemax={SEGMENTS.length}
      aria-valuenow={valueNow}
      aria-valuetext={valueText}
      className={cn('w-full', className)}
    >
      {/* Phone: only the phase you are in has a name. Four labels the operator
          is not currently waiting on is four labels of noise on a 360px screen. */}
      <div className="mb-2 flex items-center gap-2 sm:hidden">
        <span className={cn('truncate text-[12px] leading-4 font-medium', style.text)}>
          {current?.label ?? RUN_PHASE_LABELS[state.phase]}
        </span>
        <span className="tabular ml-auto flex-none text-[12px] leading-4 text-faint">
          {valueNow > 0 ? `${valueNow} of ${SEGMENTS.length}` : `0 of ${SEGMENTS.length}`}
        </span>
      </div>

      <div className="mb-2 hidden gap-1.5 sm:grid sm:grid-cols-5">
        {SEGMENTS.map((segment, i) => {
          const isCurrent = index === i
          return (
            <span
              key={segment.key}
              className={cn(
                'flex min-w-0 items-center gap-1.5 text-[12px] leading-4',
                isCurrent ? cn('font-medium', style.text) : 'text-faint'
              )}
            >
              {isCurrent && style.pulse ? <span className="pulse-dot" aria-hidden="true" /> : null}
              <span className="truncate">{segment.label}</span>
            </span>
          )
        })}
      </div>

      {/* The track is a picture of the labels above it, so it adds nothing to
          the accessibility tree — `aria-valuetext` already says the same thing
          in a sentence. */}
      <div aria-hidden="true" className="grid grid-cols-5 gap-1.5">
        {SEGMENTS.map((segment, i) => {
          const done = index > i
          const isCurrent = index === i
          return (
            <span
              key={segment.key}
              className={cn(
                'h-[3px] rounded-full transition-colors duration-300',
                done && 'bg-live',
                isCurrent && style.fill,
                isCurrent && style.pulse && 'animate-pulse',
                !done && !isCurrent && 'bg-s3'
              )}
            />
          )
        })}
      </div>

      <p className="mt-2 truncate text-[13px] leading-[18px] text-muted">{caption}</p>
    </div>
  )
})
